"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VerpackungAndKonfektionierungPriceCalculator = void 0;
const echain_math_1 = require("@igus/echain-math");
const echain_shared_articles_oo_1 = require("@igus/echain-shared-articles-oo");
const echain_shared_articles_1 = require("@igus/echain-shared-articles");
const echain_shared_pricing_1 = require("@igus/echain-shared-pricing");
const echain_shared_quantities_1 = require("@igus/echain-shared-quantities");
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
const ConfigurationDiscounter_1 = require("./ConfigurationDiscounter");
const echain_shared_configurations_1 = require("@igus/echain-shared-configurations");
class VerpackungAndKonfektionierungPriceCalculator {
    /**
     * Retrieves price information for the whole configuration.
     *
     * @param args The args to estimate the prices.
     * @returns The estimated price information.
     */
    static getAllPrices(args) {
        const configuration = args.configuration;
        const verpackung = configuration.getVerpackungenClone().toArray()[0];
        const konfektionierungen = configuration.getKonfektionierungenClone();
        const discounts = args.discounts || [];
        const country = args.country;
        const currency = args.currency;
        const fallbackCountry = args.fallbackCountry || 'DE';
        const fallbackCurrency = args.fallbackCurrency || 'EUR';
        const count = configuration.getQuoteCount() || 1;
        if (verpackung) {
            // country/currency tupels by priority
            const countryCurrencyTupels = [
                [country, currency],
                [country, fallbackCurrency],
                [fallbackCountry, currency],
                [fallbackCountry, fallbackCurrency],
            ];
            // try each tupel until done
            for (const countryCurrencyTupel of countryCurrencyTupels) {
                const tryCountry = countryCurrencyTupel[0];
                const tryCurrency = countryCurrencyTupel[1];
                const verpackungPriceVK = echain_shared_pricing_1.PriceUtils.getCountryPrice(verpackung.preis, tryCountry, tryCurrency);
                // only if country/currency is defined for all kind of selling prices (VK)
                if (verpackungPriceVK != null) {
                    // weave in optional EK and original price
                    const verpackungPriceEK = echain_shared_pricing_1.PriceUtils.getCountryPrice(verpackung.preisEK, tryCountry, tryCurrency);
                    // create the info object
                    const info = {
                        verpackungPriceVK,
                        verpackungPriceEK,
                        totalVerpackungKonfektionierungPriceVK: count * verpackungPriceVK,
                        usedCountry: tryCountry,
                        usedCurrency: tryCurrency,
                    };
                    if (verpackungPriceEK != null) {
                        info.totalVerpackungKonfektionierungPriceEK = count * verpackungPriceEK;
                    }
                    if (configuration.hasAnyNonPlaceholderLeitung()) {
                        const konfektionierungPriceVK = this.getKonfektionierungTotalPriceVK(configuration, tryCountry, tryCurrency);
                        if (konfektionierungPriceVK == null) {
                            // only return Verpackungspreis if a Konfektionierungspreis exists for a configuration with Leitungen
                            continue;
                        }
                        else {
                            info.konfektionierungPriceVK = konfektionierungPriceVK;
                            info.totalVerpackungKonfektionierungPriceVK += count * konfektionierungPriceVK;
                            // weave in optional EKs
                            if (konfektionierungen != null && konfektionierungen.count() > 0) {
                                const konfektionierungPriceEK = this.getKonfektionierungTotalPriceEK(configuration, tryCountry, tryCurrency);
                                if (konfektionierungPriceEK != null) {
                                    info.konfektionierungPriceEK = konfektionierungPriceEK;
                                    if (info.totalVerpackungKonfektionierungPriceEK != null) {
                                        info.totalVerpackungKonfektionierungPriceEK += count * konfektionierungPriceEK;
                                    }
                                }
                            }
                        }
                    }
                    return ConfigurationDiscounter_1.ConfigurationDiscounter.apply(info, discounts, count);
                }
            }
        }
        // we could not find a valid country/currency combination for this configuration
        return undefined;
    }
    /**
     * Retrieve the sum of all konfektionierung selling prices.
     *
     * @param configuration The configuration to get the price for.
     * @param country The country to retrieve the price for.
     * @param currency The currency to retrieve the price for.
     * @returns The accumulated price.
     */
    static getKonfektionierungTotalPriceVK(configuration, country, currency) {
        const customKonfektionierungPrice = configuration.getCustomKonfektionierungPriceClone();
        const konfektionierungen = configuration.getKonfektionierungenClone();
        let totalPrice;
        if (customKonfektionierungPrice) {
            const price = echain_shared_pricing_1.PriceUtils.getCountryPrice(customKonfektionierungPrice, country, currency);
            if (price != null) {
                totalPrice = totalPrice || 0;
                totalPrice += price;
            }
        }
        else {
            konfektionierungen
                .toArray()
                .filter((konf) => konf.calculatedPriceVK != null)
                .forEach((konf) => {
                const price = echain_shared_pricing_1.PriceUtils.getCountryPrice(konf.calculatedPriceVK.totalPrice, country, currency);
                if (price != null) {
                    totalPrice = totalPrice || 0;
                    totalPrice += price;
                }
            });
        }
        return totalPrice;
    }
    /**
     * Retrieve the sum of all konfektionierung buying prices.
     *
     * @param configuration The configuration to get the price for.
     * @param country The country to retrieve the price for.
     * @param currency The currency to retrieve the price for.
     * @returns The accumulated price.
     */
    static getKonfektionierungTotalPriceEK(configuration, country, currency) {
        const konfektionierungen = configuration.getKonfektionierungenClone();
        let totalPrice;
        konfektionierungen
            .toArray()
            .filter((konf) => konf.calculatedPriceEK != null)
            .forEach((konf) => {
            const price = echain_shared_pricing_1.PriceUtils.getCountryPrice(konf.calculatedPriceEK.totalPrice, country, currency);
            if (price != null) {
                totalPrice = totalPrice || 0;
                totalPrice += price;
            }
        });
        return totalPrice;
    }
    /**
     * Retrieves whether all properties are set so that a price for Verpackung can be calculated.
     *
     * Price calculation for Verpackung has internal set limits. The calculation itself still
     * functions, but Igus rules are that they have to be checked manually.
     *
     * @param configuration The configuration to test.
     * @param verpackung The Verpackung to test. If not set the Verpackung of the configuration is
     * tested.
     * @returns If the price can be calculated or not.
     */
    static canValidCalculationBePerformed(configuration, verpackung) {
        var _a;
        // init Verpackung
        if (!verpackung) {
            verpackung = configuration.getVerpackungenClone().toArray()[0];
            if (!verpackung)
                throw new Error(`${this.className}.canValidCalculationBePerformed: no Verpackung to test`);
        }
        // return true if Karton
        if (verpackung instanceof echain_shared_articles_oo_1.Karton) {
            return true;
        }
        // Twisterband has no Biegeradius, so we cannot calculate the price
        if ((0, echain_shared_articles_1.isTwisterband)((_a = configuration.getKetteClone()) === null || _a === void 0 ? void 0 : _a.toModel())) {
            return false;
        }
        let chainLength;
        let biegeradius;
        let aussenhoehe;
        let aussenbreite;
        const { type, config } = (0, echain_shared_configurations_1.asTypedConfigurationUnion)(configuration.toModel());
        switch (type) {
            case 'Empty':
            case 'Espool':
            case 'EspoolChild':
                // We can't calculate Verpackung and Konfektionierung
                break;
            case 'Kette': {
                const kette = (0, echain_shared_articles_oo_1.A)(config.Kette);
                chainLength = kette.getMostPreciseEffektiveLaenge();
                biegeradius = kette.biegeradius;
                aussenhoehe = kette.aussenhoehe;
                aussenbreite = kette.aussenbreite;
                break;
            }
            default:
                (0, echain_shared_ts_utils_1.assertUnreachable)(type);
        }
        // check Palette && Trommel
        if (!(chainLength === null || chainLength === void 0 ? void 0 : chainLength.m)) {
            return false;
        }
        if (!biegeradius)
            throw new Error(`${this.className}.canValidCalculationBePerformed: kette.biegeradius is undefined`);
        if (!aussenhoehe)
            throw new Error(`${this.className}.canValidCalculationBePerformed: kette.aussenhoehe is undefined`);
        if (!aussenbreite)
            throw new Error(`${this.className}.canValidCalculationBePerformed: kette.aussenbreite is undefined`);
        const weight = configuration.getWeightExceptVerpackungRinneAndMontageSet();
        const verpackungKerndurchmesser = verpackung instanceof echain_shared_articles_oo_1.Trommel ? verpackung.kerndurchmesser : echain_shared_quantities_1.Length.m(0);
        const ketteKerndurchmesser_mm = echain_math_1.VerpackungMath.calcKetteKerndurchmesser_mm(verpackungKerndurchmesser === null || verpackungKerndurchmesser === void 0 ? void 0 : verpackungKerndurchmesser.mm, biegeradius.mm);
        const ketteWindungen = echain_math_1.VerpackungMath.calcKetteWindungen(ketteKerndurchmesser_mm, aussenhoehe.mm, chainLength.mm);
        if (verpackung instanceof echain_shared_articles_oo_1.Palette) {
            const aussendurchmesserKette_mm = echain_math_1.VerpackungMath.calcAussendurchmesserKetteForPalette_mm(ketteKerndurchmesser_mm, ketteWindungen, aussenhoehe.mm);
            return (Boolean(aussendurchmesserKette_mm) &&
                this.canCalcPriceForPalette(weight, echain_shared_quantities_1.Length.mm(aussendurchmesserKette_mm), chainLength));
        }
        else if (verpackung instanceof echain_shared_articles_oo_1.Trommel) {
            const aussendurchmesserKette_mm = echain_math_1.VerpackungMath.calcAussendurchmesserKetteForTrommel_mm(ketteKerndurchmesser_mm, ketteWindungen, aussenhoehe.mm);
            return (Boolean(aussendurchmesserKette_mm) &&
                this.canCalcPriceForTrommel(weight, aussenbreite, biegeradius, echain_shared_quantities_1.Length.mm(aussendurchmesserKette_mm), chainLength));
        }
        else {
            throw new Error(`${this.className}.canValidCalculationBePerformed: invalid verpackung instance`);
        }
    }
    /**
     * Returns whether a price can be calculated or has to be defined by project team
     *
     * @see https://igusdev.atlassian.net/wiki/pages/viewpage.action?pageId=24576015
     *
     * Base calculation is:
     *   Should be defined by project team if one of these rules match:
     *     Wkette > 2000 kg
     *     DaKette > 2500 mm
     *     Kettenlänge > 200.000 mm
     */
    static canCalcPriceForPalette(gesamtgewichtKette, aussendurchmesserKette, laengeKette) {
        return !(gesamtgewichtKette.kg > 2000 || aussendurchmesserKette.mm > 2000 || laengeKette.mm > 200000);
    }
    /**
     * Returns whether a price can be calculated or has to be defined by project team
     *
     * @see https://igusdev.atlassian.net/wiki/pages/viewpage.action?pageId=24576015
     *
     * Base calculation is:
     *   Should be defined by project team if one of these rules match:
     *     Wkette > 2000 kg
     *     Kettenbreite BA >300 mm
     *     Biegeradius der Kette > 300 mm
     *     DaKette > 2500 mm
     *     Kettenlänge > 200.000 mm
     */
    static canCalcPriceForTrommel(gesamtgewichtKette, aussenbreiteKette, biegeradiusKette, aussendurchmesserKette, laengeKette) {
        return !(gesamtgewichtKette.kg > 2000 ||
            aussenbreiteKette.mm > 450 ||
            biegeradiusKette.mm > 300 ||
            aussendurchmesserKette.mm > 2500 ||
            laengeKette.mm > 200000);
    }
}
exports.VerpackungAndKonfektionierungPriceCalculator = VerpackungAndKonfektionierungPriceCalculator;
VerpackungAndKonfektionierungPriceCalculator.className = 'VerpackungAndKonfektionierungPriceCalculator';
//# sourceMappingURL=VerpackungAndKonfektionierungPriceCalculator.js.map